import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['tab', 'panel', 'footerPanel'];
  static classes = ['active', 'inactive'];
  // Opt-in: reserve the default panel's height on all panels so switching to a
  // shorter tab doesn't shift the layout. No-op unless the value is set.
  static values = { equalizeHeight: Boolean };

  connect() {
    if (!this.equalizeHeightValue) return;
    // The tabs can mount inside a host that starts display:none (e.g. a closed
    // slide-over), where connect() measures height 0. Observe the container and
    // reserve once it first gains a nonzero size; reserveHeight() then stops the
    // observer. Works identically when already visible at connect.
    this.heightObserver = new ResizeObserver(() => this.reserveHeight());
    this.heightObserver.observe(this.element);
  }

  disconnect() {
    this.heightObserver?.disconnect();
    this.heightObserver = null;
  }

  // Lock every panel to the height of the panel visible at reserve time (the
  // default tab — e.g. the discussion), so toggling never collapses the
  // container. No-op (and keeps observing) until a nonzero height is available.
  reserveHeight() {
    const reference = this.panelTargets.find((panel) => !panel.classList.contains('hidden'));
    const height = reference ? reference.offsetHeight : 0;
    if (height <= 0) return;

    this.panelTargets.forEach((panel) => {
      panel.style.minHeight = `${height}px`;
    });
    // Captured a valid height — stop watching.
    this.heightObserver?.disconnect();
    this.heightObserver = null;
  }

  select({ params: { index } }) {
    this.tabTargets.forEach((tab, i) => {
      const isActive = i === index;
      // Keep aria-selected in sync (not just the CSS classes) so assistive tech
      // reports the active tab — required by the WAI-ARIA tabs pattern.
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive) {
        tab.classList.remove(...this.inactiveClasses);
        tab.classList.add(...this.activeClasses);
      } else {
        tab.classList.remove(...this.activeClasses);
        tab.classList.add(...this.inactiveClasses);
      }
    });

    this.panelTargets.forEach((panel, i) => {
      panel.classList.toggle('hidden', i !== index);
    });

    // Optional paired footers (e.g. a modal's pinned footer slot): a caller may
    // render one `data-tabs-target="footerPanel"` per tab, in the SAME order as
    // the panels. No footerPanel targets → empty array → no-op (backward-compat
    // with callers that have none, e.g. the planning phase panel).
    this.footerPanelTargets.forEach((footer, i) => {
      footer.classList.toggle('hidden', i !== index);
    });
  }
}
